import type { ProductContextId } from '@vs-shop/schemas/id/ProductsContextId';
import { Context, Effect, HashMap, HashSet, Layer, Option, Stream, SubscriptionRef } from 'effect';

import type { TaxonCode, TaxonFilterStateSet } from '@vs-shop/schemas/product/filter/state/taxon';
import { ContextTag } from '../context';

export type ContextualTaxonFilterStateSet = HashMap.HashMap<ProductContextId, TaxonFilterStateSet>;

export const emptyTaxonSet = HashSet.empty<TaxonCode>();

export const createTaxonFiltersStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualTaxonFilterStateSet>,
  context: ProductContextId
) => {
  return SubscriptionRef.changes(ref).pipe(
    Stream.changes,
    Stream.map((contextMap) => HashMap.get(contextMap, context)),
    Stream.map(Option.getOrElse(() => emptyTaxonSet)),
    Stream.changes
  );
};

export const createIsTaxonSelectedStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualTaxonFilterStateSet>,
  context: ProductContextId,
  taxonCode: TaxonCode
) =>
  SubscriptionRef.changes(ref).pipe(
    Stream.map((contextMap) => HashMap.get(contextMap, context)),
    Stream.map(
      Option.match({
        onNone: () => false,
        onSome: (taxonSet) => HashSet.has(taxonSet, taxonCode),
      })
    )
  );

export const createTaxonHasActiveFiltersStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualTaxonFilterStateSet>,
  context: ProductContextId
) => createTaxonFiltersStream(ref, context).pipe(Stream.map((taxons) => HashSet.size(taxons) > 0));

export class TaxonFilterService extends Context.Service<TaxonFilterService>()(
  ContextTag.TaxonFilterService,
  {
    make: Effect.gen(function* () {
      const emptyContextMap = HashMap.empty<ProductContextId, TaxonFilterStateSet>();

      const filtersRef =
        yield* SubscriptionRef.make<ContextualTaxonFilterStateSet>(emptyContextMap);

      const getTaxons = (context: ProductContextId) =>
        Effect.gen(function* () {
          const contextMap = yield* SubscriptionRef.get(filtersRef);
          return HashMap.get(contextMap, context).pipe(Option.getOrElse(() => emptyTaxonSet));
        });

      const addTaxon = (context: ProductContextId, taxonCode: TaxonCode) =>
        SubscriptionRef.update(filtersRef, (contextMap) => {
          const currentTaxons = HashMap.get(contextMap, context).pipe(
            Option.getOrElse(() => emptyTaxonSet)
          );
          const updatedTaxons = HashSet.add(currentTaxons, taxonCode);
          return HashMap.set(contextMap, context, updatedTaxons);
        });

      const removeTaxon = (context: ProductContextId, taxonCode: TaxonCode) =>
        SubscriptionRef.update(filtersRef, (contextMap) => {
          const currentTaxons = HashMap.get(contextMap, context).pipe(
            Option.getOrElse(() => emptyTaxonSet)
          );
          const updatedTaxons = HashSet.remove(currentTaxons, taxonCode);

          if (HashSet.size(updatedTaxons) === 0) {
            return HashMap.remove(contextMap, context);
          }

          return HashMap.set(contextMap, context, updatedTaxons);
        });

      // Multi-select: adds/removes a taxon. The backend search endpoint
      // currently returns 0 for multiple taxons (it doesn't OR a `taxon` CSV the
      // way `brand` does) — that's a known BE bug being fixed backend-side, so the
      // FE keeps proper multi-select rather than working around it client-side.
      const toggleTaxon = (context: ProductContextId, taxonCode: TaxonCode) =>
        Effect.gen(function* () {
          const currentTaxons = yield* getTaxons(context);
          if (HashSet.has(currentTaxons, taxonCode)) {
            yield* removeTaxon(context, taxonCode);
          } else {
            yield* addTaxon(context, taxonCode);
          }
        });

      const clearTaxons = (context: ProductContextId) =>
        SubscriptionRef.update(filtersRef, HashMap.remove(context));

      const setTaxons = (context: ProductContextId, taxons: TaxonFilterStateSet) =>
        SubscriptionRef.update(filtersRef, HashMap.set(context, taxons));

      const getTaxonsStream = (context: ProductContextId) =>
        createTaxonFiltersStream(filtersRef, context);

      const isTaxonSelectedStream = (context: ProductContextId, taxonCode: TaxonCode) =>
        createIsTaxonSelectedStream(filtersRef, context, taxonCode);

      const hasActiveFiltersStream = (context: ProductContextId) =>
        createTaxonHasActiveFiltersStream(filtersRef, context);

      return {
        filtersRef,
        getTaxons,
        addTaxon,
        removeTaxon,
        toggleTaxon,
        clearTaxons,
        setTaxons,
        getTaxonsStream,
        isTaxonSelectedStream,
        hasActiveFiltersStream,
      };
    }),
  }
) {
  static readonly layer = Layer.effect(this, this.make);
}
